import React, { useState, useEffect } from 'react';
import { SAMPLE_COLLECTION_GUIDELINES_URL } from '../constants/publicAssets';
import './onyx/product-widgets-base.css';
import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  LocationOn,
  LocalShipping,
  Assignment,
  CheckCircle
} from '@mui/icons-material'
import { AppModal } from './AppModal'
import { Button } from './onyx/Button'
import { Field, TextInput, TextArea, Select, Autocomplete } from './onyx/Field'
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete'
import { Loader } from '@googlemaps/js-api-loader'
import './sample-collection-submission.css'

const steps = ['Overview', 'Contact Information', 'Review & Submit', 'Confirmation']

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
  'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
]

export const SampleCollectionSubmission = ({ 
  open, 
  onClose, 
  sampleCollection, 
  onSubmit,
  userProfile 
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: sampleCollection?.phone_number || '',
    preferred_contact_method: 'phone',
    special_instructions: ''
  })

  // Initialize Google Maps
  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places']
    })

    loader.load().catch(error => {
      console.error('Error loading Google Maps:', error)
    })
  }, [])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await onSubmit(sampleCollection.id, formData)
      handleNext() // Move to confirmation step
    } catch (error) {
      console.error('Submission failed:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setActiveStep(0)
    setFormData({
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip_code: '',
      phone_number: sampleCollection?.phone_number || '',
      preferred_contact_method: 'phone',
      special_instructions: ''
    })
    onClose()
  }

  const isStepValid = () => {
    switch (activeStep) {
      case 1:
        return formData.address_1 && formData.city && formData.state && 
               formData.zip_code && formData.phone_number
      case 2:
        return true // Review step is always valid
      default:
        return true
    }
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <OverviewStep sampleCollection={sampleCollection} />
      case 1:
        return (
          <ContactInformationStep 
            formData={formData} 
            onInputChange={handleInputChange} 
          />
        )
      case 2:
        return (
          <ReviewStep 
            formData={formData} 
            sampleCollection={sampleCollection} 
          />
        )
      case 3:
        return <ConfirmationStep sampleCollection={sampleCollection} />
      default:
        return null
    }
  }

  return (
    <AppModal 
      open={open} 
      onClose={handleClose} 
      title="Place Sample Collection Order"
      maxWidth="md" 
      fullWidth
      showCloseButton={false}
      className="sample-collection-submission-dialog"
      actions={
        activeStep === 3 ? (
          <Button type="button" variant="primary" size="large" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              size="large"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="primary"
              size="large"
              onClick={activeStep === 2 ? handleSubmit : handleNext}
              disabled={!isStepValid() || submitting}
            >
              {activeStep === 2 ? (submitting ? 'Placing Order...' : 'Place Order') : 'Next'}
            </Button>
          </>
        )
      }
    >
      <Typography variant="subtitle1" color="text.secondary" className="mb-2">
        {sampleCollection?.study_title}
      </Typography>
      
      <Stepper activeStep={activeStep} className="mb-4">
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {renderStepContent()}
    </AppModal>
  )
}

const OverviewStep = ({ sampleCollection }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      What to Expect
    </Typography>
    
    <List>
      <ListItem>
        <ListItemIcon>
          <LocationOn color="primary" />
        </ListItemIcon>
        <ListItemText 
          primary="Address & Contact Information"
          secondary="We'll need your address and phone number so a mobile phlebotomist can contact you to schedule your appointment."
        />
      </ListItem>
      
      <ListItem>
        <ListItemIcon>
          <LocalShipping color="primary" />
        </ListItemIcon>
        <ListItemText 
          primary="Sample Collection Kit"
          secondary="After your order is placed, we'll ship you a collection kit with a FedEx tracking number."
        />
      </ListItem>
      
      <ListItem>
        <ListItemIcon>
          <Assignment color="primary" />
        </ListItemIcon>
        <ListItemText 
          primary="Preparation Guidelines"
          secondary="Please follow our sample collection guidelines, which includes an 8 hours fast."
        />
      </ListItem>
    </List>
    
    <Alert severity="warning" className="mt-1">
      <Typography variant="body2">
        <strong>Important:</strong> Please review our sample collection guidelines before continuing! 

        <Button
          type="button"
          variant="ghost"
          className="mt-1"
          onClick={() => window.open(SAMPLE_COLLECTION_GUIDELINES_URL, '_blank')}
        >
          Sample Collection Guidelines (PDF)
        </Button>
      </Typography>
    </Alert>
  </Box>
)

/**
 * Address autocomplete — Onyx Field + native onyx-input (no MUI floating label).
 */
function AddressAutocompleteInput({ formData, onInputChange }) {
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['address']
    },
    debounce: 300,
  })

  const handleSelect = async (address) => {
    setValue(address, false)
    clearSuggestions()

    try {
      const results = await getGeocode({ address })
      await getLatLng(results[0])

      const addressComponents = results[0].address_components
      let streetNumber = ''
      let route = ''
      let city = ''
      let state = ''
      let zipCode = ''

      addressComponents.forEach(component => {
        const types = component.types
        if (types.includes('street_number')) {
          streetNumber = component.long_name
        }
        if (types.includes('route')) {
          route = component.long_name
        }
        if (types.includes('locality')) {
          city = component.long_name
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.short_name
        }
        if (types.includes('postal_code')) {
          zipCode = component.long_name
        }
      })

      onInputChange('address_1', `${streetNumber} ${route}`.trim())
      onInputChange('city', city)
      onInputChange('state', state)
      onInputChange('zip_code', zipCode)
    } catch (error) {
      console.error('Error getting geocode:', error)
    }
  }

  return (
    <Field
      label="Address Line 1"
      htmlFor="scs-address-1"
      required
      help="Start typing to see address suggestions"
    >
      <Autocomplete
        className="scs-address-autocomplete"
        options={data}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option
          }
          return option?.description || ''
        }}
        filterOptions={(x) => x}
        freeSolo
        value={formData.address_1}
        onChange={(_event, newValue) => {
          if (newValue && typeof newValue === 'object') {
            handleSelect(newValue.description)
          } else if (typeof newValue === 'string') {
            onInputChange('address_1', newValue)
          }
        }}
        onInputChange={(_event, newInputValue) => {
          setValue(newInputValue)
          onInputChange('address_1', newInputValue)
        }}
        renderInput={(params) => (
          <div
            ref={params.InputProps.ref}
            className="scs-address-autocomplete__control"
          >
            <input
              {...params.inputProps}
              id="scs-address-1"
              className="onyx-input"
              disabled={!ready}
              required
              placeholder="Start typing your address…"
              autoComplete="street-address"
            />
            {params.InputProps.endAdornment}
          </div>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.place_id} className="scs-autocomplete-option">
            {option.description}
          </li>
        )}
        noOptionsText={status === 'OK' ? 'No addresses found' : 'Start typing to search…'}
        slotProps={{
          paper: { className: 'scs-address-autocomplete__paper' },
        }}
      />
    </Field>
  )
}

export const ContactInformationStep = ({ formData, onInputChange }) => {
  const zipInvalid =
    formData.zip_code.length > 0 && formData.zip_code.length !== 5
  const phoneInvalid =
    formData.phone_number.length > 0 && formData.phone_number.length !== 10

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Contact Information
      </Typography>
      
      <div className="scs-form-grid" data-testid="scs-contact-fields">
        <AddressAutocompleteInput
          formData={formData}
          onInputChange={onInputChange}
        />
        
        <Field label="Address Line 2 (Optional)" htmlFor="scs-address-2">
          <TextInput
            id="scs-address-2"
            value={formData.address_2}
            onChange={(e) => onInputChange('address_2', e.target.value)}
            placeholder="Apt, suite, unit, etc."
            autoComplete="address-line2"
          />
        </Field>
        
        <div className="scs-address-row">
          <Field label="City" htmlFor="scs-city" required>
            <TextInput
              id="scs-city"
              value={formData.city}
              onChange={(e) => onInputChange('city', e.target.value)}
              required
              autoComplete="address-level2"
            />
          </Field>
          
          <Field label="State" htmlFor="scs-state" required>
            <Select
              id="scs-state"
              value={formData.state}
              onChange={(e) => onInputChange('state', e.target.value)}
              required
              autoComplete="address-level1"
            >
              <option value="" disabled>
                Select…
              </option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </Field>
          
          <Field
            label="ZIP Code"
            htmlFor="scs-zip"
            required
            error={zipInvalid ? 'ZIP code must be 5 digits' : undefined}
          >
            <TextInput
              id="scs-zip"
              value={formData.zip_code}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5)
                onInputChange('zip_code', value)
              }}
              required
              inputMode="numeric"
              maxLength={5}
              pattern="[0-9]{5}"
              autoComplete="postal-code"
              aria-invalid={zipInvalid || undefined}
            />
          </Field>
        </div>
        
        <Divider />
        
        <Field
          label="Phone Number"
          htmlFor="scs-phone"
          required
          help={
            phoneInvalid
              ? undefined
              : 'The mobile phlebotomist will use this to contact you'
          }
          error={phoneInvalid ? 'Phone number must be 10 digits' : undefined}
        >
          <TextInput
            id="scs-phone"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10)
              onInputChange('phone_number', value)
            }}
            required
            inputMode="numeric"
            maxLength={10}
            pattern="[0-9]{10}"
            autoComplete="tel-national"
            aria-invalid={phoneInvalid || undefined}
          />
        </Field>
        
        <Field
          label="Preferred Contact Method"
          htmlFor="scs-contact-method"
        >
          <Select
            id="scs-contact-method"
            value={formData.preferred_contact_method}
            onChange={(e) => onInputChange('preferred_contact_method', e.target.value)}
          >
            <option value="phone">Phone Call</option>
            <option value="text">Text Message</option>
            <option value="either">Either</option>
          </Select>
        </Field>
        
        <Divider />

        <Field
          label="Special Instructions (Optional)"
          htmlFor="scs-instructions"
          help="Any special instructions for the phlebotomist (e.g., gate code, best time to contact)"
        >
          <TextArea
            id="scs-instructions"
            value={formData.special_instructions}
            onChange={(e) => onInputChange('special_instructions', e.target.value)}
            rows={3}
          />
        </Field>
      </div>
    </Box>
  )
}

const ReviewStep = ({ formData, sampleCollection }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Review Your Information
    </Typography>
    
    <Box className="mt-2">
      <Typography variant="subtitle1" gutterBottom>
        <strong>Study:</strong> {sampleCollection?.study_title}
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom>
        <strong>Address:</strong>
      </Typography>
      <Typography variant="body2" className="ml-2 mb-2">
        {formData.address_1}<br />
        {formData.address_2 && <>{formData.address_2}<br /></>}
        {formData.city}, {formData.state} {formData.zip_code}
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom>
        <strong>Phone:</strong> {formData.phone_number}
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom>
        <strong>Preferred Contact:</strong> {formData.preferred_contact_method}
      </Typography>
      
      {formData.special_instructions && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Special Instructions:</strong>
          </Typography>
          <Typography variant="body2" className="ml-2">
            {formData.special_instructions}
          </Typography>
        </>
      )}
    </Box>
    
    <Alert severity="warning" className="mt-3 scs-review-alert">
      By submitting, you confirm that the information above is accurate and you're ready to proceed with sample collection.
    </Alert>
  </Box>
)

const ConfirmationStep = ({ sampleCollection }) => (
  <Box className="text-center">
    <CheckCircle className="mb-2 scs-confirmation-icon" />
    
    <Typography variant="h5" gutterBottom>
      Order Placed Successfully!
    </Typography>
    
    <Typography variant="body1" className="mb-3">
      Your sample collection order has been placed and is now being processed.
    </Typography>
    
    <Alert severity="success" className="text-left">
      <Typography variant="body2">
        <strong>What happens next:</strong>
      </Typography>
      <List dense>
        <ListItem className="scs-confirmation-list-item">
          <ListItemText primary="• We're processing your order with our mobile phlebotomy service" />
        </ListItem>
        <ListItem className="scs-confirmation-list-item">
          <ListItemText primary="• A mobile phlebotomist will contact you to schedule your appointment" />
        </ListItem>
        <ListItem className="scs-confirmation-list-item">
          <ListItemText primary="• You'll receive a FedEx tracking number for your collection kit" />
        </ListItem>
        <ListItem className="scs-confirmation-list-item">
          <ListItemText primary="• You'll receive a confirmation email with all details" />
        </ListItem>
      </List>
    </Alert>
  </Box>
)
